/**
 * boardSubjects.ts — the subjects a contract's documents can be generated for, at either scope.
 *
 * LIFTED OUT OF `useContractAgreements` WITHOUT A CHANGE OF BEHAVIOUR, so that the one thing this
 * file decides can be tested. The read used to be an inline `queryFn` inside the hook, which
 * `vitest` cannot reach — `include: ['src/**\/*.test.ts']`, `environment: 'node'` — and the answer
 * it now has to give depends on WHICH SCOPE it was asked for. A rule that picks between two
 * junction sets and must leave one of them byte-identical is not a rule to leave uncovered.
 *
 * THE TWO SCOPES ARE TWO DOORS ONTO ONE MODEL [D-B45]:
 *
 * - `'setup'` is the SETUP BOARD's subjects — the id-earliest junction's loans and nothing else
 *   [D-B17]. It is unchanged in every character, down to the order the builder is chained in, and
 *   it keeps the key it has always had.
 * - `'all'` is the RENEWAL DOOR's — every junction's loans, so the Insurance tab can offer the
 *   signing ceremony for a policy year the setup canvas is not about. It has a key of its own, so
 *   the two cache entries can never be one. IT IS ALSO THE ONLY SCOPE THAT READS `loans`, the
 *   driver's standing operational expense advances (owner's ruling, 2026-09-16): their Schedule is
 *   executed in the app now, and the door it is executed from is Contract Insights rather than the
 *   setup canvas.
 *
 * ONE DERIVATION SERVES BOTH, ON THE INSURANCE CHAIN. What differs between the two junction reads
 * is a `LIMIT 1` and nothing else: the select is the same string, the embeds are the same embeds,
 * and the rows are mapped by the same function — so a slot minted from the Insurance tab and a slot
 * minted from the setup board are the same shape, derived the same way, and `buildBoardSlots`
 * cannot tell them apart.
 *
 * THE GENERAL LOANS ARE A FOURTH READ, NOT A WIDENING OF THAT ONE. They hang off the CONTRACT and
 * not off any junction, so there is no limit to drop and no ordering to share; `'setup'` never
 * issues the read at all and gets `loans: []`, which is [D-B17] kept exactly where it was.
 * `boardSubjectGeneralLoansFrom` mirrors `boardSubjectLoansFrom` for the same reason that one
 * exists: what a subject MEANS is settled in one function, not at each call site.
 */

import { supabase } from '@/integrations/supabase/client';
import {
    earliestJunction,
    type EarliestJunctionQuery,
} from '@/components/contracts/setup/data/insurance';

import type { LegacyAgreementRow, PartnerAgreementRow } from './boardSlots';

export type { PartnerAgreementRow };

/**
 * WHICH JUNCTIONS A BOARD IS ABOUT.
 *
 * `'setup'` IS THE DEFAULT EVERYWHERE, and the default is the narrow one on purpose: a surface that
 * has not thought about renewals gets the scope [D-B17] settled, rather than silently gaining a
 * second year's rows.
 */
export type JunctionScope = 'setup' | 'all';

/**
 * The subjects entry — the contract's agreement rows and the SETUP junction's insurance loans.
 *
 * EXPORTED FOR THE SAME REASON THE CONTRACT KEY IS. Nothing that writes insurance invalidated
 * this entry before [D-B17], and it now has to: the slots are scoped to one junction and its
 * loans, so recording a loan, linking a policy or unlinking one moves what the board may draw.
 * Those writers live in `InsuranceArea` and in the policy dossier's `insuranceCommit`, and a key
 * hand-spelled at each of them is a silent no-op the moment this string moves.
 */
export const agreementBoardSubjectsKey = (contractId: number) =>
    ['agreement-board-subjects', contractId] as const;

/** Prefix form, for a writer that does not know which contract it touched. */
export const AGREEMENT_BOARD_SUBJECTS_KEY = ['agreement-board-subjects'] as const;

/**
 * The cache entry for one contract's subjects AT ONE SCOPE [D-B45].
 *
 * THE SETUP KEY IS RETURNED UNCHANGED, NOT REBUILT AROUND A SCOPE SEGMENT. Widening the shared key
 * is the failure this whole option exists to avoid: the setup board's docblock records contract
 * 137's "✓ and Not on file" contradiction, where one row graded a sealed renewal loan while its
 * detail described the unpapered original, and that came from a read that saw more junctions than
 * the surface reading it was about. So `'setup'` is `agreementBoardSubjectsKey` itself — the same
 * array, element for element — and the widened scope is what carries a segment.
 *
 * THE SCOPE SEGMENT GOES LAST, UNDER THE SAME PREFIX, AND THAT IS THE DESIGN RATHER THAN A HABIT.
 * React Query matches an invalidation by PREFIX, so `['agreement-board-subjects', 137]` reaches
 * `['agreement-board-subjects', 137, 'all']` as well: the six writers that already invalidate
 * `AGREEMENT_BOARD_SUBJECTS_KEY`, and `refreshBoard`'s own per-contract invalidation, refresh the
 * renewal door for free. A key spelled `['agreement-board-subjects-all', …]` would be a second
 * cache entry that every existing writer silently misses.
 */
export function agreementBoardSubjectsKeyFor(
    contractId: number,
    scope: JunctionScope,
): readonly (string | number)[] {
    return scope === 'setup'
        ? agreementBoardSubjectsKey(contractId)
        : [...agreementBoardSubjectsKey(contractId), scope];
}

/**
 * The junction rows this scope is about, in the order they were recorded.
 *
 * `'setup'` IS `earliestJunction` AND NOTHING ELSE — the same helper `fetchContractInsurance` uses,
 * so the rule that setup describes the id-earliest junction is spelled once for every client read
 * of it and cannot drift into two surfaces grading different years of cover.
 *
 * `'all'` KEEPS THE ORDERING AND DROPS ONLY THE LIMIT. Not ordering would be the cheaper diff and
 * the wrong one: PostgREST returns rows in whatever order the plan produced, the loans become slots
 * in arrival order, and `buildBoardSlots` labels a contract's several insurance rows `#1`, `#2` by
 * ARRAY POSITION — so an unordered read renumbers a driver's policy years between two renders of
 * the same page. `id` and not `created_at`, for the reason `earliestJunction` gives: 73 of the 84
 * production contracts on a renewal chain share one backfilled `created_at` across every junction.
 */
export function scopedJunctions<T extends EarliestJunctionQuery<T>>(
    query: T,
    scope: JunctionScope,
): T {
    if (scope === 'setup') return earliestJunction(query);
    return query.order('id', { ascending: true });
}

/**
 * One `insurance_loan_deposits` row as the embed hands it back.
 *
 * SPELLED OUT RATHER THAN `any`, because this one is arithmetic. PostgREST returns a to-one embed
 * as an object and a to-many as an array, and the deposit's transaction is to-one by its foreign
 * key — but the shape is decided by the request rather than by the schema, so both readings are
 * accepted here exactly as `first()` accepts both below.
 */
export interface LoanDepositEmbed {
    transactions?: { amount?: number | null } | Array<{ amount?: number | null }> | null;
}

/**
 * One `insurance_loans` row as the embed hands it back — the five things a SLOT needs of it.
 *
 * SPELLED OUT RATHER THAN `any`, for the reason `LoanDepositEmbed` is: two of these fields are the
 * limbs the instrument is derived from, and nothing in an `any` can be wrong. Everything but the id
 * is optional because a select that stopped asking for one of them must fail HERE — at the mapper,
 * where the answer would silently become "advance" — rather than in a component reading a field
 * that was never fetched.
 */
export interface LoanEmbed {
    id: number;
    amount?: number | null;
    insurance_loan_deposits?: LoanDepositEmbed[] | null;
    /** Read for the ids alone: the server's limb is `NOT EXISTS`, so a count is all this needs. */
    insurance_loan_instalments?: Array<{ id: number }> | null;
    /**
     * The legacy paper row. PostgREST returns a to-one embed as an object and a to-many as an
     * array; this one is to-many by shape and one-per-loan by unique index, so both readings
     * collapse to the same row and both are accepted.
     */
    insurance_loan_agreements?: LegacyAgreementRow | LegacyAgreementRow[] | null;
}

/** One `insurance_contracts` row — a junction, and the loans hanging off it. */
export interface JunctionEmbed {
    id: number;
    insurance_loans?: LoanEmbed[] | null;
}

/** One `loan_instalments` row as the embed hands it back — the repayment rate and when it starts. */
export interface GeneralLoanInstalmentEmbed {
    id: number;
    amount?: number | null;
    /**
     * A `date` COLUMN, CARRIED AS THE STRING IT ALREADY IS. It is a calendar day and not an
     * instant, so nothing here parses it: a `new Date(iso)` would move it a day west of Greenwich,
     * and the business-day helpers are what read it wherever it is finally shown.
     */
    starts_at?: string | null;
}

/**
 * One `loans` row as the embed hands it back — a standing operational expense advance.
 *
 * SPELLED OUT RATHER THAN `any`, for `LoanEmbed`'s reason. Everything but the id is optional so
 * that a select which stopped asking for one of these fails HERE, at the mapper, rather than in a
 * component reading a field that was never fetched.
 *
 * THE MONEY IS ON THE TRANSACTION, EXACTLY AS IT IS FOR AN INSURANCE DEPOSIT. `loans` carries no
 * amount column at all: the principal is the amount of the disbursement the row names, which is
 * why the select embeds it and the mapper reads it through. PostgREST returns a to-one embed as an
 * object and a to-many as an array, so both readings are accepted.
 */
export interface GeneralLoanEmbed {
    id: number;
    transaction_id?: number | null;
    /** `loans.description` — what the advance is FOR, and the Schedule's `{{loan.purpose}}`. */
    description?: string | null;
    interest_rate?: number | null;
    transactions?: { amount?: number | null } | Array<{ amount?: number | null }> | null;
    loan_instalments?: GeneralLoanInstalmentEmbed[] | null;
    /** The paper row — one per loan by unique index, accepted in either embed shape. */
    loan_agreements?: LegacyAgreementRow | LegacyAgreementRow[] | null;
}

/**
 * What this loan's deposits add up to — Σ of the TRANSACTIONS behind them.
 *
 * `insurance_loan_deposits` carries no amount of its own: the row is a claim that one transaction
 * paid down this loan, and the money is the transaction's. A deposit whose transaction the embed
 * did not return contributes nothing, which is the conservative reading — a total that is short
 * names the advance, and only a total that COVERS the premium names the payment record.
 *
 * NOTHING IS ROUNDED. The half-pesewa tolerance lives on the comparison in `instrumentFor`, which
 * is where the server puts it too.
 */
export function depositTotalOf(rows: LoanDepositEmbed[] | null | undefined): number {
    return (rows ?? []).reduce((total, row) => {
        const transaction = Array.isArray(row.transactions) ? row.transactions[0] : row.transactions;
        return total + (transaction?.amount ?? 0);
    }, 0);
}

/** One insurance loan as a slot subject: its money, its two instrument limbs, its legacy paper. */
export interface BoardSubjectLoan {
    id: number;
    amount: number | null;
    depositTotal: number;
    instalmentCount: number;
    agreement: LegacyAgreementRow | null;
}

/**
 * One standing operational expense advance as a slot subject.
 *
 * IT CARRIES WHAT THE SCHEDULE IS MADE OF, NOT ONLY WHAT A SLOT NEEDS. A LOAN slot needs the id and
 * the paper row and nothing else; the purpose, the rate, the principal and the repayment rows are
 * the figures the loan v7 Schedule prints, and the surfaces that name an advance read them from
 * here rather than from `get_contract_insights` — where `loans[].description` is the bank
 * TRANSACTION's narration and not `loans.description` at all.
 */
export interface BoardSubjectGeneralLoan {
    id: number;
    /** The disbursement this advance names. Null is "no transaction attached yet". */
    transactionId: number | null;
    /** The disbursement's amount — the principal. Null where the embed returned no transaction. */
    amount: number | null;
    /** `loans.description` — what the advance is for. Render-blocking on the Schedule. */
    description: string | null;
    interestRate: number | null;
    /**
     * `loan_instalments`, as read: this advance's rate PERIODS, newest `starts_at` first.
     *
     * SEVERAL ROWS, NOT ONE. The table is UNIQUE on `(loan_id, starts_at)`, so an advance whose
     * weekly rate was re-cut carries a row per period; `[0]` is the rate in force today and the
     * EARLIEST `starts_at` is the day repayment began. `amount` is a weekly figure and `starts_at`
     * a calendar day carried as its string.
     */
    instalments: GeneralLoanInstalmentEmbed[];
    agreement: LegacyAgreementRow | null;
}

export interface BoardSubjects {
    contractAgreements: Array<LegacyAgreementRow & { type: string }>;
    insuranceLoans: BoardSubjectLoan[];
    /**
     * THE CONTRACT'S STANDING ADVANCES — AT `'all'` ONLY, AND `[]` AT `'setup'` [D-B17].
     *
     * The key is present at both scopes and empty at one of them, rather than optional: a consumer
     * that has to test for the field would read "not fetched" and "none recorded" as one answer,
     * and those are two different sentences on a board.
     */
    loans: BoardSubjectGeneralLoan[];
    /**
     * EVERY schedule referent on this contract, in id order — PLURAL, AND THE PICK IS SEPARATE.
     *
     * `maybeSingle` IS THE WRONG SHAPE NOW. The referent is UNIQUE on `(contract_id, partner_id)`
     * with a second partial unique on `(contract_id) WHERE signed_at IS NULL`, so a contract whose
     * vehicle legitimately changed hands holds the previous owner's SEALED historical row AND the
     * current owner's — two rows, and a `maybeSingle` read of them is an error rather than an
     * answer.
     *
     * WHICH ONE THE BOARD IS ABOUT NEEDS A FACT THIS READ DOES NOT HAVE: the partner who owns the
     * vehicle TODAY, which is a column on the contract row `useContractAgreements` reads. So the
     * rows travel and `pickPartnerAgreement` chooses, rather than this query gaining a parameter
     * that would also have to enter its cache key — where a reassignment would silently strand the
     * old entry.
     */
    partnerAgreements: PartnerAgreementRow[];
}

/**
 * WHICH referent this board is about — the current owner's, else the unsealed one.
 *
 * THE ORDER IS THE SERVER'S OWN [D-B48], and the two limbs answer two different questions. Every
 * READ gate keys on the STAMPED partner (who signed keeps reading their instrument after a
 * reassignment), but the driver-signature GATE keys on the vehicle's CURRENT partner and requires
 * the referent to agree — so the row this board grades, offers to generate and locks the hire
 * purchase agreement on has to be the current owner's wherever one exists.
 *
 * THE UNSEALED ROW IS THE FALLBACK RATHER THAN THE FIRST ANSWER. A reservation raised before a
 * reassignment carries the old owner's stamp until `agreement_create` re-points it, and it is the
 * row a regenerated schedule will reuse — so a board with no current-owner row shows that one and
 * its state, instead of announcing that nothing has been raised while a document is out.
 *
 * A SEALED ROW OF SOMEBODY ELSE'S IS NEVER THE ANSWER. It is the previous owner's executed
 * instrument: reading it as this board's would grade the contract done on a schedule signed about
 * a bargain that has ended, which is exactly what `agreement_sign` refuses with
 * `AGREEMENT_PRIOR_SIGNATURE_REQUIRED`.
 */
export function pickPartnerAgreement(
    rows: PartnerAgreementRow[] | null | undefined,
    vehiclePartnerId: number | null | undefined,
): PartnerAgreementRow | null {
    const all = rows ?? [];
    if (vehiclePartnerId !== null && vehiclePartnerId !== undefined) {
        const current = all.find((row) => row.partner_id === vehiclePartnerId);
        if (current) return current;
    }
    return all.find((row) => !row.signed_at) ?? null;
}

/**
 * THE TWO LIMBS THE INSTRUMENT IS DERIVED FROM [D-B45], on the same rows the board already read.
 *
 * The money is on the TRANSACTION and never on the link row — an `insurance_loan_deposits` row is
 * a claim on a transaction and carries no amount of its own — and the instalments are read for
 * their ids alone, because the limb is `NOT EXISTS` and a count is all a client can want from it.
 * Neither embed is ordered: a sum and a length do not care what order they arrive in, unlike the
 * two orderings the read applies, where a consumer takes `[0]`.
 */
const SUBJECT_LOANS_SELECT =
    'id, insurance_loans(id, amount, ' +
    'insurance_loan_deposits(transactions(amount)), ' +
    'insurance_loan_instalments(id), ' +
    'insurance_loan_agreements(id, signed_at, agreement_url, origin))';

/**
 * THE STANDING ADVANCE, AND EVERYTHING ITS SCHEDULE IS MADE OF (owner's ruling, 2026-09-16).
 *
 * `transactions!transaction_id` NAMES THE FOREIGN KEY RATHER THAN LEAVING PostgREST TO PICK ONE,
 * which is the spelling every read of this arc in the repo uses — an embed that resolves by table
 * name alone is one added relationship away from resolving somewhere else. The money is the
 * disbursement's, because `loans` holds no amount of its own — the same arrangement
 * `insurance_loan_deposits(transactions(amount))` above reads for an insurance deposit.
 *
 * THE INSTALMENT ROWS ARE READ WHOLE, NOT COUNTED. The insurance limb wants ids alone because the
 * server's own limb is `NOT EXISTS`; this one wants the weekly rate and the day it starts, which
 * are two of the Schedule's printed particulars and the two fields an advance can be edited on.
 *
 * AND THEY ARE THE EMBED THAT HAS TO BE ORDERED. `loan_instalments` is UNIQUE on
 * `(loan_id, starts_at)` and not on `loan_id` — several rate periods per advance is the shape the
 * table was built for, and the `23505` adopt path every writer keeps depends on it — so a consumer
 * taking `[0]` off an unordered embed takes whichever row Postgres returned first. The read below
 * orders it `starts_at` DESCENDING, which is `get_contract_insights`' own
 * `ORDER BY li.starts_at DESC`: two surfaces reading one advance's rate must not disagree about
 * which period is current. The agreements embed is one row per loan by unique index, and `first()`
 * collapses either embed shape onto that row.
 */
const SUBJECT_GENERAL_LOANS_SELECT =
    'id, transaction_id, description, interest_rate, ' +
    'loan_agreements(id, signed_at, agreement_url, origin), ' +
    'loan_instalments(id, amount, starts_at), ' +
    'transactions!transaction_id(amount)';

/** What `'setup'` answers with, in the shape the read would have returned [D-B17]. */
const NO_GENERAL_LOANS: { data: GeneralLoanEmbed[]; error: null } = { data: [], error: null };

// PostgREST returns a to-one embed as an object and a to-many as an array; the legacy agreement
// embeds are to-many by shape and one-per-subject by the unique indexes the migration added, so
// both readings collapse to the same row.
const first = (
    value: LoanEmbed['insurance_loan_agreements'] | GeneralLoanEmbed['loan_agreements'],
): LegacyAgreementRow | null => (Array.isArray(value) ? value[0] ?? null : value ?? null);

/**
 * The junction rows, flattened into slot subjects — ONE DERIVATION, BOTH SCOPES.
 *
 * IT IS A `flatMap` OVER JUNCTIONS RATHER THAN A LOOK AT THE FIRST ONE, and that is what lets the
 * same function answer for a `LIMIT 1` read and an unlimited one. Under `'setup'` the array it
 * walks has exactly one member, so the output is what it always was; under `'all'` it is the
 * contract's whole renewal chain, in junction order, each junction's loans in id order within it.
 */
export function boardSubjectLoansFrom(
    junctions: JunctionEmbed[] | null | undefined,
): BoardSubjectLoan[] {
    return (junctions ?? []).flatMap((junction) =>
        (junction.insurance_loans ?? []).map((loan) => ({
            id: loan.id,
            amount: loan.amount ?? null,
            depositTotal: depositTotalOf(loan.insurance_loan_deposits),
            instalmentCount: (loan.insurance_loan_instalments ?? []).length,
            agreement: first(loan.insurance_loan_agreements),
        })),
    );
}

/**
 * The `loans` rows, flattened into slot subjects — `boardSubjectLoansFrom`'s mirror.
 *
 * IT IS A `map` AND NOT A `flatMap` BECAUSE THERE IS NO JUNCTION TO WALK. A standing advance hangs
 * off the contract directly, which is the whole reason it is a separate read: there is no policy
 * year to widen and no `LIMIT 1` to drop, so the only scope question is whether the read goes out
 * at all.
 *
 * THE PRINCIPAL COMES OFF THE TRANSACTION AND IS NEVER INVENTED. A loan whose disbursement the
 * embed did not return has a null amount rather than a zero — the same distinction
 * `boardSubjectLoansFrom` keeps for `insurance_loans.amount`, and `money()` on the slot prints
 * nothing at all for it rather than `GH₵0.00`.
 */
export function boardSubjectGeneralLoansFrom(
    rows: GeneralLoanEmbed[] | null | undefined,
): BoardSubjectGeneralLoan[] {
    return (rows ?? []).map((row) => {
        const transaction = Array.isArray(row.transactions) ? row.transactions[0] : row.transactions;
        return {
            id: row.id,
            transactionId: row.transaction_id ?? null,
            amount: transaction?.amount ?? null,
            description: row.description ?? null,
            interestRate: row.interest_rate ?? null,
            instalments: row.loan_instalments ?? [],
            agreement: first(row.loan_agreements),
        };
    });
}

/**
 * The subjects a document can be generated for, at the scope asked for.
 *
 * TWO THINGS THIS READ STOPPED LOOKING AT [D-B17], and both were the same defect. At `'setup'` it
 * read EVERY junction's loans, so a contract on its third year of cover minted one insurance-loan slot
 * per year and the manifest's "Insurance loan agreement" row graded all of them at once — that is
 * contract 137's "✓ and Not on file" contradiction, where the server graded the sealed renewal loan
 * while the row's detail described the unpapered original. And it read `loans`, the driver's
 * standing general loans, which are not part of setting a contract up at all: they are opened and
 * papered from Contract Insights, at whatever point in the contract's life the driver borrows.
 *
 * THE SETUP BOARD READS NO `loans`, AND THE `'all'` BOARD NOW DOES (owner's ruling, 2026-09-16).
 * [D-B17] was never about the table — it is about the CANVAS: a driver's standing advance is not
 * part of setting a contract up, it is opened whenever they borrow, and a LOAN slot on the setup
 * manifest would be a row nobody could act on and an instrument inside `RATE_INSTRUMENTS`' reach
 * over a debt the contract was never written around. All of that still holds at `'setup'`, which
 * returns `loans: []` without issuing the read. What changed is the OTHER door: the advance's
 * Schedule is executed in the app from Contract Insights now, so the board that door reads has to
 * mint the slot it is executed on.
 *
 * THE FOURTH READ IS NOT A WIDENING OF THE JUNCTION ONE. `loans.contract_id` is a column on the
 * contract, so the rows come back in one filtered read ordered by id — the same ordering the
 * junction read applies and for the same reason: `buildBoardSlots` labels several rows of one type
 * `#1`, `#2` by ARRAY POSITION, and an unordered read renumbers a driver's advances between two
 * renders of the same page.
 *
 * SEVERAL LOANS ON ONE JUNCTION STILL YIELD SEVERAL SLOTS, which is correct at both scopes: they
 * are all debts of the year that junction covers.
 *
 * THE THIRD READ IS SCOPE-INDEPENDENT [D-B48]. `JunctionScope` is about insurance junctions and
 * nothing else, and a contract has one vehicle and therefore one live Vehicle Schedule at either
 * scope — so the schedule referent is read the same way for the setup canvas and the renewal door,
 * beside the contract-agreements read it most resembles.
 */
export async function fetchAgreementBoardSubjects(
    contractId: number,
    scope: JunctionScope,
): Promise<BoardSubjects> {
    const [agreements, insurance, partnerAgreements, generalLoans] = await Promise.all([
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
            .from('contract_agreements')
            .select('id, type, signed_at, agreement_url, origin')
            .eq('contract_id', contractId),
        scopedJunctions(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (supabase as any)
                .from('insurance_contracts')
                .select(SUBJECT_LOANS_SELECT)
                .eq('contract_id', contractId)
                /*
                 * WITHIN THE JUNCTION, THE ORDER THE SERVER READS IN — the same two lines the
                 * envelope read carries. `scopedJunctions` settles WHICH junctions; these settle
                 * the order of what hangs off them, and unordered embeds are how two surfaces
                 * describing one junction still disagree. The loans become slots in the order they
                 * arrive, and `get_contract_setup_status` grades `ORDER BY il.id LIMIT 1`
                 * (`20260804030000_canvas_order_driver_first.sql`); `first()` above takes `[0]` of
                 * the agreements embed, which is one row per loan by unique index but is ordered
                 * anyway rather than relying on that.
                 */
                .order('id', { referencedTable: 'insurance_loans', ascending: true })
                .order('id', {
                    referencedTable: 'insurance_loans.insurance_loan_agreements',
                    ascending: true,
                }),
            scope,
        ),
        /*
         * PLURAL, AND ORDERED, FOR THE REASON `pickPartnerAgreement` STATES. The two unique
         * indexes admit a sealed historical row beside the current owner's, so the read takes them
         * all and the pick chooses; `id` ascending is what makes the fallback limb deterministic
         * where a contract somehow holds more than one unsealed row.
         *
         * THE CASH PRICE COPIED ONTO THE ROW IS NOT READ [D-B59]: the schedule's slot states the
         * contract's own `cash_price`, which is what `agreement_create` copies at generation.
         */
        supabase
            .from('contract_partner_agreements')
            .select('id, partner_id, vehicle_id, signed_at, agreement_url, origin, management_rate')
            .eq('contract_id', contractId)
            .order('id', { ascending: true }),
        /*
         * THE STANDING ADVANCES, AT `'all'` AND NOWHERE ELSE [D-B17]. At `'setup'` no request goes
         * out at all — not a filtered one, not a `LIMIT 0` — because the setup canvas is not a door
         * onto a driver's borrowing and a read it never uses is a round trip it should not make.
         * The empty answer is spelled here rather than at the mapper so the two arms come back the
         * same shape.
         */
        scope === 'all'
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ? (supabase as any)
                .from('loans')
                .select(SUBJECT_GENERAL_LOANS_SELECT)
                .eq('contract_id', contractId)
                .order('id', { ascending: true })
                /*
                 * NEWEST RATE PERIOD FIRST, WHICH IS THE SERVER'S OWN ORDER. An advance can carry
                 * several `loan_instalments` rows — the table is UNIQUE on `(loan_id, starts_at)` —
                 * and `get_contract_insights` emits its `instalments` array
                 * `ORDER BY li.starts_at DESC`. A consumer reading the current weekly rate takes
                 * `[0]` off either array, so an unordered embed here is how two surfaces come to
                 * print two different weekly figures for one advance.
                 */
                .order('starts_at', { referencedTable: 'loan_instalments', ascending: false })
            : Promise.resolve(NO_GENERAL_LOANS),
    ]);

    for (const result of [agreements, insurance, partnerAgreements, generalLoans]) {
        if (result.error) throw result.error;
    }

    return {
        contractAgreements: (agreements.data ?? []) as BoardSubjects['contractAgreements'],
        insuranceLoans: boardSubjectLoansFrom(insurance.data as JunctionEmbed[] | null),
        partnerAgreements: (partnerAgreements.data ?? []) as PartnerAgreementRow[],
        loans: boardSubjectGeneralLoansFrom(generalLoans.data as GeneralLoanEmbed[] | null),
    };
}
